import React, { useState, useContext, useEffect, useCallback } from 'react';
import AuthContext from '../context/AuthContext';

// The full state machine (server.js) has more granular steps than shown here.
// This UI intentionally collapses adjacent steps (e.g. collection_pending ->
// dog_collected -> walk_in_progress) into single actions for a usable MVP,
// rather than inventing extra screens the pilot doesn't need yet.
const WALKER_STEPS = [
  { from: 'confirmed', to: 'walker_en_route', label: 'Heading to collect the dog' },
  { from: 'walker_en_route', to: 'walk_in_progress', label: 'Dog collected — walk started' },
  { from: 'walk_in_progress', to: 'returning', label: 'Heading back' },
  { from: 'returning', to: 'completed', label: 'Dog returned — complete walk' }
];

const STATUS_LABELS = {
  confirmed: 'Confirmed — waiting for walker to set off',
  walker_en_route: 'Walker is on the way',
  collection_pending: 'Walker arriving to collect the dog',
  dog_collected: 'Dog collected',
  walk_in_progress: 'Walk in progress',
  returning: 'Walker is bringing the dog back',
  return_pending: 'Dog being returned',
  dog_returned: 'Dog returned home'
};

const ActiveWalk = ({ onCompleted }) => {
  const { getToken, user } = useContext(AuthContext);
  const [walk, setWalk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [payError, setPayError] = useState('');

  const fetchActive = useCallback(async () => {
    try {
      const response = await fetch('/api/walks/active', {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setWalk(data);
      if (data?.booking_id) {
        const payRes = await fetch(`/api/payments/booking/${data.booking_id}`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        setPayment(await payRes.json());
      }
    } catch (err) {
      console.error('Error fetching active walk:', err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchActive();
    const interval = setInterval(fetchActive, 5000);
    return () => clearInterval(interval);
  }, [fetchActive]);

  const payNow = async () => {
    setPayError('');
    try {
      const response = await fetch('/api/payments/checkout', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: walk.booking_id })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      window.location.assign(data.checkoutUrl);
    } catch (err) {
      setPayError(err.message);
    }
  };

  const advance = async (toStatus) => {
    try {
      await fetch(`/api/walks/${walk.id}/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: toStatus })
      });
      if (toStatus === 'completed' && onCompleted) {
        onCompleted(walk);
      }
      await fetchActive();
    } catch (err) {
      console.error('Error updating walk status:', err);
    }
  };

  if (loading) return null;
  if (!walk) return null;

  const isWalker = user.user_type === 'walker';
  const nextStep = WALKER_STEPS.find(s => s.from === walk.status);
  const isPaid = payment?.status === 'completed';
  const paymentBlocksStart = walk.status === 'confirmed' && !isPaid;

  return (
    <div className="active-walk-panel">
      <h3>🐾 Active walk — {walk.dog_name}</h3>
      <p>Status: {STATUS_LABELS[walk.status] || walk.status}</p>

      {payment && (
        <p className="payment-status">
          {isPaid ? `✓ Paid £${Number(payment.amount_gbp).toFixed(2)}` : 'Payment: not yet paid'}
        </p>
      )}

      {!isWalker && paymentBlocksStart && (
        <div>
          {payError && <p className="error">{payError}</p>}
          <button className="confirm" onClick={payNow}>Pay for this walk</button>
        </div>
      )}

      {isWalker && paymentBlocksStart && (
        <p className="owner-note">Waiting for the owner to pay before you can start this walk.</p>
      )}

      {isWalker && !paymentBlocksStart && nextStep && (
        <button className="confirm" onClick={() => advance(nextStep.to)}>{nextStep.label}</button>
      )}
      {!isWalker && !paymentBlocksStart && (
        <p className="owner-note">
          {walk.status === 'walk_in_progress' ? `${walk.walker_name} is out walking ${walk.dog_name} now.` : `Waiting on ${walk.walker_name}.`}
        </p>
      )}
    </div>
  );
};

export default ActiveWalk;
